import { Link, useParams, useLocation } from "react-router-dom";
import { useUiStore } from "@/stores/ui.store";
import { usePrefetchRoute } from "@/hooks/usePrefetchRoute";

const mainNavItems = [
  {
    label: "Home",
    href: "/dashboard",
    icon: (active: boolean) => (
      <svg className="h-6 w-6" fill={active ? "currentColor" : "none"} viewBox="0 0 24 24" strokeWidth={active ? 0 : 1.5} stroke="currentColor">
        {active ? (
          <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 0 1-.53 1.28h-1.44v7.44a.75.75 0 0 1-.75.75h-4.5a.75.75 0 0 1-.75-.75v-4.875a.375.375 0 0 0-.375-.375h-2.25a.375.375 0 0 0-.375.375V21.25a.75.75 0 0 1-.75.75h-4.5a.75.75 0 0 1-.75-.75v-7.44H2.81a.75.75 0 0 1-.53-1.28l8.69-8.69Z" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
        )}
      </svg>
    ),
  },
  {
    label: "Properties",
    href: "/properties",
    icon: (active: boolean) => (
      <svg className="h-6 w-6" fill={active ? "currentColor" : "none"} viewBox="0 0 24 24" strokeWidth={active ? 0 : 1.5} stroke="currentColor">
        {active ? (
          <path d="M11.584 2.376a.75.75 0 0 1 .832 0l9 6a.75.75 0 1 1-.832 1.248L12 3.901 3.416 9.624a.75.75 0 0 1-.832-1.248l9-6Z M20.25 10.332v9.918a.75.75 0 0 1-.75.75H4.5a.75.75 0 0 1-.75-.75v-9.918l8.25-5.5 8.25 5.5Z" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V3.545M12.75 21h7.5V10.75M2.25 21h1.5m18 0h-18M2.25 9l4.5-1.636M18.75 3l-1.5.545m0 6.205 3 1m1.5.5-1.5-.5M6.75 7.364V3h-3v18m3-13.636 10.5-3.819" />
        )}
      </svg>
    ),
  },
];

const chartIcon = (active: boolean) => (
  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={active ? 2 : 1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
  </svg>
);

const listIcon = (active: boolean) => (
  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={active ? 2 : 1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>
);

export function BottomNav() {
  const pathname = useLocation().pathname;
  const propertyId = pathname.match(/\/property\/([^/]+)/)?.[1] ?? null;
  const unitId = pathname.match(/\/unit\/([^/]+)/)?.[1] ?? null;
  const { setSidebarOpen } = useUiStore();
  const { prefetchRoute } = usePrefetchRoute();

  const showPropertyNav = !!propertyId && (pathname.startsWith(`/property/${propertyId}`));

  const propertyNavItems = showPropertyNav
    ? [
        { label: "Details", href: unitId ? `/property/${propertyId}/unit/${unitId}` : `/property/${propertyId}`, icon: listIcon },
        { label: "Analytics", href: unitId ? `/property/${propertyId}/analytics?unitId=${unitId}` : `/property/${propertyId}/analytics`, icon: chartIcon },
      ]
    : null;

  const navItems = propertyNavItems ?? mainNavItems;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-900/90 backdrop-blur-xl lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="relative flex items-center justify-around px-2 sm:px-4 py-2">
        {/* Overlay back button when on property – only 2 nav items, back floats on the left */}
        {showPropertyNav && (
          <Link
            to="/properties"
            onMouseEnter={() => prefetchRoute("/properties")}
            onTouchStart={() => prefetchRoute("/properties")}
            onClick={() => setSidebarOpen(false)}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-md text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-500 hover:shadow-lg active:scale-95 transition-all"
            aria-label="Back to properties"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </Link>
        )}

        <div className={`flex flex-1 items-center justify-around ${showPropertyNav ? "pl-12" : ""}`}>
          {navItems.map((item) => {
            let isActive: boolean;
            if (showPropertyNav && propertyId) {
              const base = unitId ? `/property/${propertyId}/unit/${unitId}` : `/property/${propertyId}`;
              const analyticsPath = `/property/${propertyId}/analytics`;
              const isAnalyticsItem = item.href.startsWith(analyticsPath);
              isActive = isAnalyticsItem
                ? pathname === analyticsPath || pathname.startsWith(analyticsPath + "?")
                : pathname === base || (pathname.startsWith(base + "/") && !pathname.startsWith(analyticsPath));
            } else {
              isActive = pathname === item.href || (item.href !== "/properties" && pathname.startsWith(item.href + "/"));
            }
            return (
              <Link
                key={item.href}
                to={item.href}
                onMouseEnter={() => prefetchRoute(item.href)}
                onTouchStart={() => prefetchRoute(item.href)}
                onClick={() => setSidebarOpen(false)}
                className="flex flex-1 flex-col items-center gap-0.5 py-1 transition-colors min-w-0"
              >
                <div className={`flex items-center justify-center rounded-full px-4 sm:px-5 py-1 transition-all duration-200 ${
                  isActive ? "bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400" : "text-slate-400 dark:text-slate-500"
                }`}>
                  {item.icon(isActive)}
                </div>
                <span className={`text-[10px] font-semibold transition-colors truncate max-w-full ${
                  isActive ? "text-primary-600 dark:text-primary-400" : "text-slate-400 dark:text-slate-500"
                }`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
